import { Form, Select } from "antd";
import { Key, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { NameType, Payload, ValueType } from "recharts/types/component/DefaultTooltipContent";
import styled from "styled-components";
import _ from "lodash";
import { EstadisticasRequest } from "../EstadisticasApi/useGraficaAPI";
import { years } from "../Utilidades/Options";

// Estilos
const Container = styled.div`
position:relative;
`;
const TextTitle = styled.h2`
  margin-top: 0.3rem;
  font-size: 1.1rem;
  font-weight: 550;
  color: var(--primary-color);
  text-align: "center";
  padding-bottom: 1.5rem;
`;
const ChartContainer = styled.div`
position:relative;
width: 100%;
background: white;
padding-top: 2rem;
padding-right:3rem;
padding-bottom: 2rem;
overflow: auto;
`;
const Total = styled.div`
display: flex;
.hidden
{
  color:#9e9e9e!important;
  text-decoration:line-through;
}
& > div
{
  position: sticky;
  left: 0;
  padding-left: 1rem;
  display:flex;
  gap: 1rem;
  span 
  {
    cursor:pointer;
  }
}
margin-left: 2rem;
margin-top: 2rem;
justify-content: space-between;
align-items: center;
`;

export type GraficaViewerData = { yName: string, dataArr: { key: number, value: number }[] };

export interface GraficaProps {
  title?: string;
  data: GraficaViewerData[];
  xAxis: (string | number)[];
  yAxis: { name: string | number, color: string }[];
  width?: number;
  height?: number;
  request?: EstadisticasRequest;
  handleChangeGrafico: any;
  tooltipLabelFormatter?: (label: any, payload: Payload<ValueType, NameType>[]) => string;
}

const GraficoPagasImpagas = (props: GraficaProps) => {
  const [chartType, setChartType] = useState<('LINE' | 'BAR')>('BAR');
  const [hiddenY, setHiddenY] = useState<string[]>([]);
  const toggleY = (yName: string) => {
    setHiddenY(_.includes(hiddenY, yName) ? hiddenY.filter(v => v != yName) : [...hiddenY, ...[yName]]);
  }

  const getFormattedData = () => {
    const array: any = [];
    let yAxis = props.yAxis.filter(e => e.name !== "Impresas");
    for (const xName of props.xAxis) {
      const data: any = { name: xName };
      for (const i in yAxis) {
        const yName = yAxis[i].name;
        data[yName] = props.data.filter(v => v.yName == yName).at(0)?.dataArr.filter(v => v.key == xName).at(0)?.value ?? 0;
      }
      array.push(data);
    }
    return array;
  };

  const getTotals = () => {
    const totals: { key: string, value: number }[] = [];
    const data = getFormattedData();
    let cont = 0;
    props.data[0]?.dataArr.map(e => cont += e.value)
    for (const key in data.at(0)) {
      if (key == 'name') continue;
      totals.push({ key, value: _.chain(data).sumBy(key).value() });
    }
    totals.unshift({ key: "Impresas", value: cont });
    return totals;
  };

  return (
    <Container>
      <Form
        initialValues={{
          ["Year"]: props.request?.pagasImpagasYear
        }}
      >
        <Form.Item
          rules={[
            { required: true, message: "Campo requerido" }
          ]}
          required
          name={"Year"}
          label="Año"
        >
          <Select
            optionFilterProp="children"
            allowClear
            size="middle"
            style={{ width: "25%" }}
            onChange={(e) => props.handleChangeGrafico(1, e)}
          >
            {
              years.map((e: { id: Key | null | undefined; value: unknown; }) =>
                <Select.Option key={e.id} value={e.value}>{e.value}</Select.Option>
              )
            }
          </Select>
        </Form.Item>
      </Form>
      {props.title && <TextTitle>{props.title}</TextTitle>}
      <ChartContainer>
        { // Gráfico de Líneas
          chartType == 'LINE' &&
          <LineChart
            width={props.width ?? 900}
            height={props.height ?? 220}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            data={getFormattedData()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis ticks={props.xAxis} dataKey={"name"} />
            <YAxis allowDecimals={false} />
            <Tooltip labelFormatter={props.tooltipLabelFormatter} />
            <Legend />
            {props.data.map((v, k) => <Line hide={_.includes(hiddenY, props.yAxis[k]?.name)} key={k} type="monotone" dataKey={props.yAxis[k]?.name} stroke={props.yAxis[k]?.color} />)}
          </LineChart>
        }

        { // Gráfico de Barras
          chartType == 'BAR' &&
          <BarChart
            width={props.width ?? 900}
            height={props.height ?? 220}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            data={getFormattedData()}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis ticks={props.xAxis} dataKey={"name"} />
            <YAxis allowDecimals={false} />
            <Tooltip labelFormatter={props.tooltipLabelFormatter} />
            <Legend />
            {props.data.map((v, k) => <Bar hide={_.includes(hiddenY, props.yAxis[k]?.name)} key={k} type="monotone" dataKey={props.yAxis[k]?.name} fill={props.yAxis[k]?.color} />)}
          </BarChart>
        }

        { // Zona inferior del gráfico
          getTotals().length > 0 &&
          <Total>
            <div>{getTotals().map((v, k) =>
              <span className={_.includes(hiddenY, v.key) ? 'hidden' : ''}
                onClick={() => toggleY(v.key)}
                key={k}
                style={{ color: props.yAxis.filter(y => y.name == v.key).at(0)?.color }}><strong>{v.key}:</strong> {v.value}</span>)}
              <strong>Click en el dato para ocultar/mostrar</strong>
            </div>
            <Select value={chartType} onChange={(v) => setChartType(v)}>
              <Select.Option value="LINE">Gráfico de líneas</Select.Option>
              <Select.Option value="BAR">Gráfico de barras</Select.Option>
            </Select>
          </Total>
        }
      </ChartContainer>
    </Container>
  )
};

export default GraficoPagasImpagas;